import { relations } from 'drizzle-orm'
import {
  boolean,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getDateField,
  getIdReference,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { teams } from './team'
import { users } from './user'

export const pulseContactRoleEnum = pgEnum('pulse_contact_role', [
  'supervisor',
  'safety_rep',
  'hr',
  'ceo',
])

/**
 * Weekly employee pulse. Deliberately has no user reference: the score and
 * comment are anonymous, and only the team (as it was on the answering week)
 * is kept for grouping. respondentKey is an HMAC of the user and the week
 * under a secret that lives outside the database, so one worker gets one row
 * per week and can edit it, while the database alone cannot say who answered.
 */
export const pulseResponses = pgTable(
  'pulse_responses',
  {
    ...getBaseFields(),
    weekStart: getDateField().notNull(),
    respondentKey: text().notNull(),
    teamId: getNullableIdReference(() => teams.id),
    isSubcontractor: boolean().notNull().default(false),
    score: integer().notNull(),
    comment: text(),
    // Statement answers keyed by PULSE_ITEMS key; empty until the second step.
    answers: typedJsonb<Record<string, number>>('answers').notNull(),
  },
  (table) => [
    ...getBaseIndexes('pulse_responses', table),
    index('pulse_responses_week_start_idx').on(table.weekStart),
    uniqueIndex('pulse_responses_week_respondent_idx').on(
      table.weekStart,
      table.respondentKey,
    ),
  ],
)

/**
 * Opt-in request to talk to someone in person. Named by design (the worker
 * chose to be contacted), kept apart from the anonymous response, and
 * handled by management in the ERP.
 */
export const pulseContactRequests = pgTable(
  'pulse_contact_requests',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    weekStart: getDateField().notNull(),
    contactRole: pulseContactRoleEnum().notNull(),
    handledAt: getTimestampWithTimezone(),
  },
  (table) => [
    ...getBaseIndexes('pulse_contact_requests', table),
    index('pulse_contact_requests_user_id_idx').on(table.userId),
  ],
)

export const pulseResponsesRelations = relations(pulseResponses, ({ one }) => ({
  team: one(teams, {
    fields: [pulseResponses.teamId],
    references: [teams.id],
  }),
}))

export const pulseContactRequestsRelations = relations(
  pulseContactRequests,
  ({ one }) => ({
    user: one(users, {
      fields: [pulseContactRequests.userId],
      references: [users.id],
    }),
  }),
)
